<script setup>
// Prompts for a won/lost reason when a deal is closed. Resolves via events: `confirm` (reasonId)
// or `cancel`. When the pipeline marks the reason mandatory, confirm stays disabled until picked.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  reasons: { type: Array, default: () => [] },
  kind: { type: String, default: 'won' },
  required: { type: Boolean, default: false },
});
const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const dialogRef = ref(null);
const reasonId = ref('');

const options = computed(() => [
  ...(!props.required
    ? [{ value: '', label: t('WASAPI_CRM.CLOSE_REASON.NONE') }]
    : []),
  ...props.reasons.map(r => ({ value: r.id, label: r.name })),
]);

const open = () => {
  reasonId.value = '';
  dialogRef.value?.open();
};
const confirm = () => emit('confirm', reasonId.value || null);
const cancel = () => emit('cancel');
defineExpose({ open, close: () => dialogRef.value?.close() });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      kind === 'won'
        ? t('WASAPI_CRM.CLOSE_REASON.WON_TITLE')
        : t('WASAPI_CRM.CLOSE_REASON.LOST_TITLE')
    "
    :confirm-button-label="t('WASAPI_CRM.CLOSE_REASON.CONFIRM')"
    :disable-confirm-button="required && !reasonId"
    @confirm="confirm"
    @close="cancel"
  >
    <div class="flex flex-col gap-2">
      <p class="text-sm text-n-slate-11">
        {{ t('WASAPI_CRM.CLOSE_REASON.HINT') }}
      </p>
      <ComboBox
        v-model="reasonId"
        :options="options"
        :placeholder="t('WASAPI_CRM.CLOSE_REASON.PLACEHOLDER')"
        class="w-full"
      />
    </div>
  </Dialog>
</template>
